import { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  useParams,
  useNavigate,
  useLocation,
  generatePath,
} from 'react-router-dom';
import {
  SidePanel,
  SidePanelType,
  ajvResolver,
  responseErrorsResolver,
  ResponseError,
  ButtonPrimary,
  ButtonSecondary,
  Popup,
  SelectItemProps,
  InputText,
  Autocomplete,
  ClickOutsideDetector,
  apiAbortController,
  IconLoader,
  PermissionEnum,
  PermissionDenied,
} from '@ui/src';
import { useAppSelector } from '@store';
import { LinkedClientsFormValues, PspIntegrationOrganization } from './types';
import {
  useLazyGetPspPartnerIntegrationsOrganizationsByNameQuery,
  useBindPspPartnerExternalOrganizationMutation,
  useLazyGetPspPartnerLinkedClientsQuery,
} from './store/services';
import useGetOrganizations from './helpers/use-get-organizations';

import { BankRoutes } from '../../routes';

import cn from './bank-and-psp.module.css';

const schema = {
  type: 'object',
  properties: {
    externalId: {
      type: 'string',
      minLength: 1,
      errorMessage: 'External Customer is required',
    },
    organizationId: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Customer is required',
    },
  },
  required: ['externalId', 'organizationId'],
};

let timeout: NodeJS.Timeout;

const CreateLinkedCustomer = () => {
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [globalError, setGlobalError] = useState('');
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState<
    SelectItemProps | undefined
  >(undefined);

  const [
    externalOrganizationsListVisible,
    setExternalOrganizationsListVisible,
  ] = useState(false);
  const [externalOrganizationSearch, setExternalOrganizationSearch] =
    useState('');
  const [pspIntegrationOrganizationsList, setPspIntegrationOrganizationsList] =
    useState<PspIntegrationOrganization[]>([]);

  const [getLinkedClients] = useLazyGetPspPartnerLinkedClientsQuery();

  const [
    getPspIntegrationOrganizations,
    { isLoading: organizationsIsLoading, isFetching: organizationsIsFetching },
  ] = useLazyGetPspPartnerIntegrationsOrganizationsByNameQuery();

  const [
    bindExternalOrganization,
    { isLoading: requestInProgress, isSuccess },
  ] = useBindPspPartnerExternalOrganizationMutation();

  const { generalInfo, linkedClientsTakeCount } = useAppSelector(
    (state) => state.bankAndPsp.info,
  );

  const { activePermissions } = useAppSelector(
    (state) => state.configurationDictionaries,
  );

  const {
    control,
    handleSubmit,
    setError,
    formState: { errors, isDirty },
  } = useForm<LinkedClientsFormValues>({
    resolver: ajvResolver(schema),
  });

  const navigate = useNavigate();
  const { bankId } = useParams();
  const { search } = useLocation();

  const getPspIntegrationOrganizationsAction = async ({
    name,
  }: {
    name: string;
  }) => {
    apiAbortController.abortPendingRequest();

    const response = await getPspIntegrationOrganizations(
      `?pspIntegrationId=${generalInfo?.pspIntegrationId}&name=${name}&take=40`,
    );

    if (response?.data?.data) {
      const nextOrganizationsList = response?.data?.data;

      setPspIntegrationOrganizationsList(nextOrganizationsList);
    }
  };

  const onSubmit = handleSubmit(async (values) => {
    setGlobalError('');

    const response = (await bindExternalOrganization({
      organizationId: values.organizationId,
      organizationType:
        selectedOrganization?.description === 'Individual'
          ? 'INDIVIDUAL'
          : 'CORPORATE',
      externalId: values?.externalId,
      active: true,
      pspPartnerId: generalInfo?.id as string,
    })) as { error?: ResponseError };

    if (response?.error) {
      responseErrorsResolver({
        errors: response.error,
        setError,
        setGlobalError,
      });
    }
  });

  const { autocompleteList, onSearch, isLoading, onClear } =
    useGetOrganizations();

  const handleClose = () => {
    setPanelIsOpen(false);
    setGlobalError('');
    setSelectedOrganization(undefined);

    setTimeout(() => {
      navigate(
        `${generatePath(BankRoutes.BANK_AND_PSP_LINKED_CUSTOMERS, { bankId: bankId as string })}${search || ''}`,
      );
    }, 100);
  };

  useEffect(() => {
    setPanelIsOpen(true);
  }, []);

  useEffect(() => {
    if (
      externalOrganizationSearch &&
      (organizationsIsLoading || organizationsIsFetching)
    ) {
      setExternalOrganizationsListVisible(true);
    }
  }, [
    externalOrganizationSearch,
    organizationsIsLoading,
    organizationsIsFetching,
  ]);

  useEffect(() => {
    if (isSuccess) {
      handleClose();

      getLinkedClients({
        id: bankId as string,
        query: `?take=${linkedClientsTakeCount}${search ? search.replace('?', '&') : ''}`,
      });
    }
  }, [isSuccess]);

  let content;

  if (
    !activePermissions?.includes(
      PermissionEnum.EXTERNAL_ORGANIZATIONS_CREATE_ACTION,
    )
  ) {
    content = <PermissionDenied />;
  } else {
    content = (
      <>
        <div className={cn.linkedCustomerFormGroup}>
          <Controller
            control={control}
            defaultValue=""
            name="pspPartnerId"
            render={({ field: { name } }) => (
              <InputText
                name={name}
                value={generalInfo?.companyName}
                title="Bank"
                placeholder=""
                error={errors?.pspPartnerId?.message}
                disabled
              />
            )}
          />
        </div>
        <div className={cn.linkedCustomerFormGroup}>
          <Controller
            control={control}
            defaultValue=""
            name="organizationId"
            render={({ field: { name, onChange, value } }) => (
              <Autocomplete
                name={name}
                value={value}
                list={autocompleteList}
                placeholder="Enter customer name or company name..."
                isLoading={isLoading}
                title="Customer"
                onSearch={(text) => onSearch(text)}
                onChange={(e) => {
                  onChange(e.target.value);

                  const organization = autocompleteList?.find(
                    (item) => item.value === e.target.value,
                  );

                  if (organization) {
                    setSelectedOrganization(organization);
                  }
                }}
                onClearClick={() => {
                  onClear();

                  setSelectedOrganization(undefined);
                }}
                error={errors?.organizationId?.message}
                fullWidth
                withClearButton
              />
            )}
          />
        </div>
        <div className={cn.linkedCustomerFormGroup}>
          <Controller
            control={control}
            defaultValue=""
            name="externalId"
            render={({ field: { onChange } }) => (
              <ClickOutsideDetector
                setOptionsListVisible={setExternalOrganizationsListVisible}
              >
                <div className={cn.searchExternalOrganizationWrapper}>
                  <InputText
                    name="searchName"
                    value={externalOrganizationSearch}
                    title="External Customer"
                    placeholder="Enter name..."
                    onChange={(e) => {
                      clearTimeout(timeout);

                      setExternalOrganizationSearch(e.target.value);

                      if (e.target.value) {
                        timeout = setTimeout(() => {
                          getPspIntegrationOrganizationsAction({
                            name: e.target.value,
                          });
                        }, 700);
                      }
                    }}
                    onFocus={() => {
                      if (pspIntegrationOrganizationsList?.length) {
                        setExternalOrganizationsListVisible(true);
                      }
                    }}
                    error={errors?.externalId?.message}
                  />
                  {externalOrganizationsListVisible && (
                    <div className={cn.organizationList}>
                      {(organizationsIsLoading || organizationsIsFetching) && (
                        <div className={cn.organizationListLoader}>
                          <IconLoader />
                        </div>
                      )}
                      {!organizationsIsLoading &&
                        !organizationsIsFetching &&
                        pspIntegrationOrganizationsList?.map((item) => (
                          <button
                            type="button"
                            className={cn.organizationButton}
                            key={item.externalId}
                            onClick={() => {
                              setExternalOrganizationsListVisible(false);
                              onChange(item.externalId);
                              setExternalOrganizationSearch(item.name);
                            }}
                          >
                            <div className={cn.organizationButtonLeft}>
                              {item.name}
                            </div>
                            <div className={cn.organizationButtonRight}>
                              {item.organizationType
                                .split('_')
                                ?.map(
                                  (organizationType) =>
                                    `${organizationType.charAt(0)}${organizationType.slice(1).toLowerCase()}`,
                                )
                                .join(' ')}
                            </div>
                            <div className={cn.externalOrganizationId}>
                              {item.externalId}
                            </div>
                          </button>
                        ))}
                    </div>
                  )}
                </div>
              </ClickOutsideDetector>
            )}
          />
        </div>
      </>
    );
  }

  return (
    <>
      <SidePanel
        title="Link Client"
        isOpen={panelIsOpen}
        type={SidePanelType.AML}
        sidepanelExpandedKey="psp-partner-link-client"
        onClose={() => {
          if (isDirty) {
            setDirtyPopupVisible(true);
          } else {
            handleClose();
          }
        }}
        errorSidepanel={globalError}
        footer={
          <>
            <ButtonPrimary
              text={requestInProgress ? 'Loading...' : 'Link'}
              disabled={requestInProgress}
              onClick={onSubmit}
              bold
            />
            <ButtonSecondary
              text="Close"
              className="button-white"
              onClick={() => {
                if (isDirty) {
                  setDirtyPopupVisible(true);
                } else {
                  handleClose();
                }
              }}
            />
          </>
        }
        closeOnClickOutside
      >
        {content}
      </SidePanel>
      <Popup
        title="You have unsaved changes"
        description=""
        onCancelClick={() => setDirtyPopupVisible(false)}
        onOkClick={handleClose}
        cancelButtonText="Cancel"
        okButtonText="Close"
        isVisible={dirtyPopupVisible}
        parent={document.getElementById('dirty-popup') as HTMLElement}
      />
      <div id="dirty-popup" />
    </>
  );
};

export default CreateLinkedCustomer;
